/**
 * Hierarquia visual de status do funil — uniforme entre lead.stage e
 * proposal.status pra dar peso adequado a cada momento da jornada.
 *
 * **Por que existe**: antes cada componente decidia sozinho a cor/peso
 * do badge. Resultado em prod (reportado pelo Alexandre 2026-04-25):
 * "Proposta aceita" e "Contrato assinado" ficavam com chip cinza
 * minúsculo — ridículo pro tamanho do evento. Agora um helper único
 * mapeia status → nível de visibilidade com 4 graus:
 *
 *   - **milestone** — eventos-marco (proposta aceita, contrato assinado,
 *     cliente provisionado). Badge sólido accent dourado + ícone +
 *     sombra + tamanho maior. Card ganha borda lateral accent + bg
 *     tonificado.
 *   - **active** — em andamento (proposta enviada, aguardando lead).
 *     Badge outline accent.
 *   - **waiting** — aguardando ação externa (lead respondendo, lead
 *     revisando contrato). Badge outline warning amarelo.
 *   - **neutral** — default/draft. Badge cinza atual.
 *   - **lost** — perdido/recusado/expirado. Badge outline destructive.
 *
 * Aplicação:
 *   - LeadStageBadge consome `getLeadStageStyle(stage)`
 *   - LeadProposalsSection (badge de status da proposta) consome
 *     `getProposalStatusStyle(status)`
 *   - Cards podem aplicar `cardClassName` quando o evento é marco
 *
 * Tudo via tokens do DS — funciona em light + dark sem hardcode de cor.
 */

import type { LeadStage } from './schemas.js';

export type StatusVisualLevel = 'milestone' | 'active' | 'waiting' | 'neutral' | 'lost';

export interface StatusVisualStyle {
  level: StatusVisualLevel;
  /** Classes pra aplicar no badge/chip (display compacto do status). */
  badgeClassName: string;
  /** Ícone que precede o label do status (emoji ou null). */
  icon: string | null;
  /** Classes opcionais pro card que contém o item — só populado quando
   * `level='milestone'`, pra reforço visual periférico (borda lateral
   * accent + bg sutil). Usado pelo card da proposta aceita ou do contrato
   * assinado pra que Karla bata o olho na lista de leads e veja
   * imediatamente onde estão os marcos. */
  cardClassName: string;
}

/** Estilos base por nível — declarados uma vez, reusados por todos os
 * status. Concentra o "como cada nível se parece" num lugar só. */
const LEVEL_STYLES: Record<StatusVisualLevel, Omit<StatusVisualStyle, 'level' | 'icon'>> = {
  milestone: {
    badgeClassName:
      'inline-flex items-center gap-1.5 rounded-md border border-accent/60 bg-accent/15 px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-accent-foreground shadow-sm',
    cardClassName: 'border-l-4 border-l-accent bg-accent/[0.04]',
  },
  active: {
    badgeClassName:
      'inline-flex items-center gap-1 rounded-md border border-accent/40 bg-accent/5 px-2 py-0.5 text-xs font-medium text-accent-foreground',
    cardClassName: '',
  },
  waiting: {
    badgeClassName:
      'inline-flex items-center gap-1 rounded-md border border-warning/40 bg-warning/5 px-2 py-0.5 text-xs font-medium text-foreground',
    cardClassName: '',
  },
  neutral: {
    badgeClassName:
      'inline-flex items-center gap-1 rounded-md border border-border bg-muted/50 px-2 py-0.5 text-xs font-medium text-muted-foreground',
    cardClassName: '',
  },
  lost: {
    badgeClassName:
      'inline-flex items-center gap-1 rounded-md border border-destructive/30 bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive',
    cardClassName: '',
  },
};

/** Mapa de stages do lead → nível visual + ícone. */
const LEAD_STAGE_LEVEL: Record<LeadStage, { level: StatusVisualLevel; icon: string | null }> = {
  new: { level: 'neutral', icon: null },
  form_sent: { level: 'waiting', icon: '⏳' },
  form_answered: { level: 'active', icon: '📋' },
  proposal_sent: { level: 'waiting', icon: '⏳' },
  // Marco do funil: lead aceitou. Próximo passo crítico (gerar contrato)
  // depende dele. Visualmente forte pra Karla não perder.
  proposal_accepted: { level: 'milestone', icon: '✅' },
  contract_sent: { level: 'waiting', icon: '⏳' },
  // Marco máximo do funil pré-provisionamento.
  signed: { level: 'milestone', icon: '✍️' },
  // Marco final — cliente ativo na plataforma.
  provisioned: { level: 'milestone', icon: '🎉' },
  lost: { level: 'lost', icon: null },
};

/** Resolve o estilo visual de um stage do lead. */
export function getLeadStageStyle(stage: LeadStage): StatusVisualStyle {
  const config = LEAD_STAGE_LEVEL[stage];
  const base = LEVEL_STYLES[config.level];
  return {
    level: config.level,
    icon: config.icon,
    badgeClassName: base.badgeClassName,
    cardClassName: base.cardClassName,
  };
}

/** Stages "terminais" do funil — cliente ativo (signed, provisioned).
 * Usado pra decidir layout pós-conversão (banner de cliente, cards
 * collapsed, ações simplificadas). */
export const TERMINAL_STAGES: readonly LeadStage[] = ['signed', 'provisioned'];

export function isTerminalStage(stage: LeadStage): boolean {
  return TERMINAL_STAGES.includes(stage);
}
